import React from 'react';
import type { BaseAutocompleteProps, BaseAutocompleteValue } from '../../types';
import { AutocompleteListItem, OptionsListAddAction } from '../components';

/**
 * Helper function to highlight matching text in search results
 * @param text - The full text to search within
 * @param query - The search query to highlight
 * @returns React elements with bold highlighting for matches
 */
export const highlightMatch = (text: string, query: string): React.ReactNode => {
  if (!query || query.length === 0) {
    return text;
  }

  const parts = text.split(new RegExp(`(${query})`, 'gi'));
  return (
    <>
      {parts.map((part, index) =>
        part.toLowerCase() === query.toLowerCase() ? <strong key={index}>{part}</strong> : part,
      )}
    </>
  );
};

export type CreateRenderOptionConfig = {
  /**
   * Optional function to map the option to a BaseAutocompleteValue
   */
  mapOptionToValue?: BaseAutocompleteProps['mapOptionToValue'];
  /**
   * Optional action displayed at the top of the dropdown list
   */
  optionsListAddAction?: {
    label: string;
    onClick: () => void;
  };
  /**
   * Adornment rendered at the top of the dropdown list (e.g. create an unregistered user button)
   */
  topOfListAdornment?: React.ReactNode;
  /**
   * Whether the avatar should be visible in the list
   */
  isAvatarVisible?: boolean;
  /**
   * Whether to highlight matching text in the options
   */
  highlightSearchText?: boolean;
  /**
   * The current search query to highlight in options
   */
  searchQuery?: string;
};

/**
 * Creates a reusable renderOption function for Autocomplete components.
 * This function handles rendering of autocomplete list items with optional
 * add actions, avatars, and custom rendering logic.
 *
 * @param config - Configuration object for the renderOption function
 * @returns A renderOption function that can be used with MUI Autocomplete
 *
 * @example
 * ```tsx
 * const renderOption = createRenderOption({
 *   isAvatarVisible: true,
 *   mapOptionToValue: (option) => option as BaseAutocompleteValue,
 *   optionsListAddAction: {
 *     label: 'Add new item',
 *     onClick: () => console.log('Add clicked'),
 *   },
 * });
 *
 * <Autocomplete renderOption={renderOption} {...otherProps} />
 * ```
 */
export const createRenderOption = (config: CreateRenderOptionConfig = {}) => {
  const {
    mapOptionToValue = (val) => val as ReturnType<BaseAutocompleteProps['mapOptionToValue']>,
    optionsListAddAction,
    topOfListAdornment,
    isAvatarVisible = true,
    highlightSearchText = false,
    searchQuery = '',
  } = config;

  // eslint-disable-next-line react/display-name
  return (
    props: React.HTMLAttributes<HTMLLIElement> & { key?: React.Key },
    option: BaseAutocompleteValue,
    state: { selected: boolean; index: number },
  ) => {
    const { key, ...optionProps } = props;

    // Default renderOption implementation
    return (
      <React.Fragment key={key}>
        {optionsListAddAction && state.index === 0 && <OptionsListAddAction {...optionsListAddAction} />}
        {topOfListAdornment && state.index === 0 && !optionsListAddAction && topOfListAdornment}
        <AutocompleteListItem
          option={mapOptionToValue(option)}
          optionProps={optionProps}
          isSelected={state.selected}
          isAvatarVisible={isAvatarVisible}
          highlightSearchText={highlightSearchText}
          searchQuery={searchQuery}
        />
      </React.Fragment>
    );
  };
};
